import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import { getMarkRange, type Editor } from '@tiptap/core';

import {
  EMPTY_LINK_FIELD_VALUES,
  EMPTY_LINK_SELECT_VALUE,
  LINK_CONTEXT_POPUP_EDGE_MARGIN,
  LINK_CONTEXT_POPUP_MAX_WIDTH,
  WYSIWYG_EDITOR_APPS,
  WYSIWYG_EDITOR_FOLDERS,
  WYSIWYG_EDITOR_ITEMS,
  WYSIWYG_EDITOR_PAGES,
} from './constants';
import { escapeHtml } from './html';
import {
  getComparableLinkFields,
  getEmptyLinkTargetAttrs,
  getItemOptionsForFilters,
  getItemOptionsForPage,
  getLinkTargetAttrs,
  getLinkTargetKindFromAttrs,
  getLinkTargetLabel,
  getLinkTargetText,
  getOptionValue,
  getPageOptionsForFolder,
  getTedInternalLinkId,
  isValidWebsiteUrl,
  serializeLinkAttributes,
  withPlaceholderOption,
} from './links';
import type {
  WysiwygActiveButton,
  WysiwygActiveLink,
  WysiwygEditorEditLinkPopupKind,
  WysiwygEditorLinkPopupConfig,
  WysiwygEditorLinkPopupKind,
  WysiwygEditorLinkTargetKind,
  WysiwygEditorMenu,
  WysiwygEditorOption,
  WysiwygLinkContext,
  WysiwygLinkFieldValues,
  WysiwygLinkTargetAttrs,
} from './types';

interface WysiwygLinkWorkflowOptions {
  getEditor: () => Editor | undefined;
  getActiveMenu: () => WysiwygEditorMenu;
  setActiveMenu: (menu: WysiwygEditorMenu) => void;
  getIsHtmlMode: () => boolean;
  getHasFocus: () => boolean;
  closeMenu: () => void;
}

type DescendantNode = Parameters<
  Parameters<Editor['state']['doc']['descendants']>[0]
>[0];

export default class WysiwygLinkWorkflow {
  @tracked
  linkFieldValues: WysiwygLinkFieldValues = { ...EMPTY_LINK_FIELD_VALUES };

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  anchorName = '';

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  initialAnchorName = '';

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  anchorEditPosition: number | null = null;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  initialLinkFieldValues: WysiwygLinkFieldValues = {
    ...EMPTY_LINK_FIELD_VALUES,
  };

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  buttonLinkEditPosition: number | null = null;

  @tracked
  activeLinkContext: WysiwygLinkContext | null = null;

  constructor(private options: WysiwygLinkWorkflowOptions) {}

  get shouldShowLinkContextPopup() {
    return Boolean(
      this.activeLinkContext &&
        !this.options.getActiveMenu() &&
        this.options.getHasFocus() &&
        !this.options.getIsHtmlMode()
    );
  }

  get activeLinkContextStyle() {
    const x = this.activeLinkContext?.x ?? 0;
    const y = this.activeLinkContext?.y ?? 0;

    return htmlSafe(
      `--wysiwyg-editor-link-context-x: ${x}px; --wysiwyg-editor-link-context-y: ${y}px;`
    );
  }

  get activeLinkPopupKind():
    | WysiwygEditorLinkPopupKind
    | WysiwygEditorEditLinkPopupKind
    | null {
    const activeMenu = this.options.getActiveMenu();

    switch (activeMenu) {
      case 'page-link':
      case 'item-link':
      case 'folder-link':
      case 'app-link':
      case 'anchor-link':
      case 'website-link':
      case 'page-link-edit':
      case 'item-link-edit':
      case 'folder-link-edit':
      case 'app-link-edit':
      case 'anchor-link-edit':
      case 'website-link-edit':
        return activeMenu;
      default:
        return null;
    }
  }

  get showRemoveLinkAction() {
    return this.isEditLinkPopupActive;
  }

  get isEditLinkPopupActive() {
    return this.activeLinkPopupKind?.endsWith('-edit') ?? false;
  }

  get activeLinkTargetKind(): WysiwygEditorLinkTargetKind | null {
    return this.activeLinkPopupKind?.replace(
      /-edit$/,
      ''
    ) as WysiwygEditorLinkTargetKind | null;
  }

  get hasLinkPopupChanges() {
    if (!this.isEditLinkPopupActive) {
      return true;
    }

    const kind = this.activeLinkTargetKind;

    if (!kind) {
      return false;
    }

    return (
      JSON.stringify(getComparableLinkFields(this.linkFieldValues, kind)) !==
      JSON.stringify(getComparableLinkFields(this.initialLinkFieldValues, kind))
    );
  }

  get isLinkPopupSubmitDisabled() {
    const kind = this.activeLinkTargetKind;

    if (!kind) {
      return true;
    }

    const hasValidTarget =
      kind === 'website-link'
        ? isValidWebsiteUrl(this.linkFieldValues['website-url'])
        : Boolean(getLinkTargetAttrs(kind, this.linkFieldValues).href);

    return (
      !hasValidTarget ||
      (this.isEditLinkPopupActive && !this.hasLinkPopupChanges)
    );
  }

  get isAnchorInsertSubmitDisabled() {
    const anchorName = this.anchorName.trim();
    const isUnchangedEdit =
      this.anchorEditPosition !== null &&
      anchorName === this.initialAnchorName.trim();

    return (
      !anchorName ||
      this.hasAnchorName(anchorName, this.anchorEditPosition) ||
      isUnchangedEdit
    );
  }

  get anchorPopupSubmitLabel() {
    return this.anchorEditPosition === null ? 'Add Anchor' : 'Save Anchor';
  }

  get selectedFolderId() {
    return getOptionValue(
      WYSIWYG_EDITOR_FOLDERS,
      this.linkFieldValues['folder-id']
    );
  }

  get pageOptions() {
    return getPageOptionsForFolder(this.selectedFolderId);
  }

  get selectedPageId() {
    return getOptionValue(this.pageOptions, this.linkFieldValues['page-id']);
  }

  get itemOptions() {
    return getItemOptionsForFilters(this.selectedFolderId, this.selectedPageId);
  }

  get selectedItemId() {
    return getOptionValue(this.itemOptions, this.linkFieldValues['item-id']);
  }

  get selectedAppId() {
    return getOptionValue(WYSIWYG_EDITOR_APPS, this.linkFieldValues['app-id']);
  }

  get anchorOptions(): WysiwygEditorOption[] {
    const anchorNames = this.getAnchorNames();

    const selectedAnchorName = this.linkFieldValues['anchor-name'];

    if (selectedAnchorName) {
      anchorNames.add(selectedAnchorName);
    }

    return [...anchorNames].map((anchorName) => ({
      value: anchorName,
      label: anchorName,
    }));
  }

  get activeLinkPopupConfig(): WysiwygEditorLinkPopupConfig | null {
    switch (this.activeLinkPopupKind) {
      case 'page-link':
      case 'page-link-edit':
        return {
          kind: this.activeLinkPopupKind,
          title: this.isEditLinkPopupActive ? 'Edit Page Link' : 'Link to Page',
          primaryActionLabel: this.isEditLinkPopupActive
            ? 'Save Link'
            : 'Add Link',
          fields: [
            {
              kind: 'select',
              label: 'Folder',
              value: this.selectedFolderId,
              options: withPlaceholderOption(
                'All Folders',
                WYSIWYG_EDITOR_FOLDERS
              ),
              testId: 'folder-id',
            },
            {
              kind: 'select',
              label: 'Page',
              value: this.selectedPageId,
              options: withPlaceholderOption('Choose Page', this.pageOptions),
              testId: 'page-id',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'item-link':
      case 'item-link-edit':
        return {
          kind: this.activeLinkPopupKind,
          title: this.isEditLinkPopupActive ? 'Edit Item Link' : 'Link to Item',
          primaryActionLabel: this.isEditLinkPopupActive
            ? 'Save Link'
            : 'Add Link',
          fields: [
            {
              kind: 'select',
              label: 'Folder',
              value: this.selectedFolderId,
              options: withPlaceholderOption(
                'All Folders',
                WYSIWYG_EDITOR_FOLDERS
              ),
              testId: 'folder-id',
            },
            {
              kind: 'select',
              label: 'Page',
              value: this.selectedPageId,
              options: withPlaceholderOption('All Pages', this.pageOptions),
              testId: 'page-id',
            },
            {
              kind: 'select',
              label: 'Item',
              value: this.selectedItemId,
              options: withPlaceholderOption('Choose Item', this.itemOptions),
              testId: 'item-id',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'folder-link':
      case 'folder-link-edit':
        return {
          kind: this.activeLinkPopupKind,
          title: this.isEditLinkPopupActive
            ? 'Edit Folder Link'
            : 'Link to Folder',
          primaryActionLabel: this.isEditLinkPopupActive
            ? 'Save Link'
            : 'Add Link',
          fields: [
            {
              kind: 'select',
              label: 'Folder',
              value: this.selectedFolderId,
              options: withPlaceholderOption(
                'Choose Folder',
                WYSIWYG_EDITOR_FOLDERS
              ),
              testId: 'folder-id',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'app-link':
      case 'app-link-edit':
        return {
          kind: this.activeLinkPopupKind,
          title: this.isEditLinkPopupActive ? 'Edit App Link' : 'Link to App',
          primaryActionLabel: this.isEditLinkPopupActive
            ? 'Save Link'
            : 'Add Link',
          fields: [
            {
              kind: 'select',
              label: 'App',
              value: this.selectedAppId,
              options: withPlaceholderOption('Choose App', WYSIWYG_EDITOR_APPS),
              testId: 'app-id',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'anchor-link':
      case 'anchor-link-edit':
        return {
          kind: this.activeLinkPopupKind,
          title: this.isEditLinkPopupActive
            ? 'Edit Anchor Link'
            : 'Link to Anchor',
          primaryActionLabel: this.isEditLinkPopupActive
            ? 'Save Link'
            : 'Add Link',
          fields: [
            {
              kind: 'select',
              label: 'Anchor',
              value: this.linkFieldValues['anchor-name'],
              options: withPlaceholderOption(
                'Choose Anchor',
                this.anchorOptions
              ),
              isDisabled: this.anchorOptions.length === 0,
              testId: 'anchor-name',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'website-link':
        return {
          kind: 'website-link',
          title: 'Link to Website',
          primaryActionLabel: 'Add Link',
          fields: [
            {
              kind: 'input',
              label: 'Website URL',
              placeholder: 'https://',
              value: this.linkFieldValues['website-url'],
              testId: 'website-url',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      case 'website-link-edit':
        return {
          kind: 'website-link-edit',
          title: 'Edit Website Link',
          primaryActionLabel: 'Save Link',
          fields: [
            {
              kind: 'input',
              label: 'Website URL',
              value: this.linkFieldValues['website-url'],
              testId: 'website-url',
            },
            {
              kind: 'input',
              label: 'Link Text',
              value: this.linkFieldValues['link-text'],
              testId: 'link-text',
            },
          ],
        };
      default:
        return null;
    }
  }

  clearContext() {
    this.activeLinkContext = null;
  }

  clearMenuState() {
    this.buttonLinkEditPosition = null;
  }

  private getAnchorNames(excludedPosition: number | null = null) {
    const anchorNames = new Set<string>();

    this.options.getEditor()?.state.doc.descendants((node, position) => {
      const anchorName = this.getAnchorName(node, position, excludedPosition);

      if (anchorName) {
        anchorNames.add(anchorName);
      }

      return true;
    });

    return anchorNames;
  }

  private getAnchorName(
    node: DescendantNode,
    position: number,
    excludedPosition: number | null
  ) {
    if (node.type.name !== 'wysiwygAnchor' || position === excludedPosition) {
      return '';
    }

    return String(node.attrs['name'] ?? node.attrs['id'] ?? '').trim();
  }

  private hasAnchorName(anchorName: string, excludedPosition: number | null) {
    return this.getAnchorNames(excludedPosition).has(anchorName);
  }

  resetAnchorState() {
    this.anchorName = '';
    this.initialAnchorName = '';
    this.anchorEditPosition = null;
  }

  syncActiveButtonLinkPrompt() {
    const activeButton = this.getActiveButton();

    if (!activeButton || activeButton.href) {
      return;
    }

    this.syncActiveLinkContext();
  }

  initializeLinkFields(
    kind: WysiwygEditorLinkPopupKind | WysiwygEditorEditLinkPopupKind
  ) {
    const editor = this.options.getEditor();
    const activeButton =
      this.getActiveButton() ??
      this.getButtonAtPosition(this.buttonLinkEditPosition);
    const activeLink = activeButton ? null : this.getActiveLink();
    const targetKind = kind.replace(
      /-edit$/,
      ''
    ) as WysiwygEditorLinkTargetKind;
    const attrs = activeButton?.attrs ??
      activeLink?.attrs ?? {
        href: editor?.getAttributes('link')['href'] ?? '',
        dataPage: editor?.getAttributes('link')['dataPage'] ?? null,
        dataItem: editor?.getAttributes('link')['dataItem'] ?? null,
        dataFolder: editor?.getAttributes('link')['dataFolder'] ?? null,
        dataApp: editor?.getAttributes('link')['dataApp'] ?? null,
      };
    const activeTargetKind =
      activeButton?.targetKind ?? activeLink?.targetKind ?? targetKind;

    this.buttonLinkEditPosition = activeButton?.from ?? null;

    if (kind.endsWith('-edit')) {
      if (activeButton) {
        editor?.commands.setTextSelection({
          from: activeButton.from + 1,
          to: activeButton.to - 1,
        });
      } else if (activeLink) {
        editor?.commands.setTextSelection({
          from: activeLink.from,
          to: activeLink.to,
        });
      } else {
        editor?.chain().focus().extendMarkRange('link').run();
      }
    }

    const selectedText =
      activeButton?.text ?? activeLink?.text ?? this.getSelectedText();

    const folderId =
      attrs.dataFolder ??
      getTedInternalLinkId(attrs.href, 'category') ??
      EMPTY_LINK_SELECT_VALUE;
    const pageId =
      attrs.dataPage ??
      getTedInternalLinkId(attrs.href, 'page') ??
      EMPTY_LINK_SELECT_VALUE;
    const itemId =
      attrs.dataItem ??
      getTedInternalLinkId(attrs.href, 'item') ??
      EMPTY_LINK_SELECT_VALUE;
    const appId =
      attrs.dataApp ??
      getTedInternalLinkId(attrs.href, 'app') ??
      EMPTY_LINK_SELECT_VALUE;
    const page = WYSIWYG_EDITOR_PAGES.find((option) => option.value === pageId);
    const item = WYSIWYG_EDITOR_ITEMS.find((option) => option.value === itemId);
    const anchorName =
      activeTargetKind === 'anchor-link' ? attrs.href.replace(/^#/, '') : '';

    const nextLinkFieldValues: WysiwygLinkFieldValues = {
      'website-url': activeTargetKind === 'website-link' ? attrs.href : '',
      'folder-id': getOptionValue(
        WYSIWYG_EDITOR_FOLDERS,
        item?.folderId ?? page?.folderId ?? folderId
      ),
      'page-id': getOptionValue(
        getPageOptionsForFolder(item?.folderId ?? page?.folderId ?? folderId),
        item?.pageId ?? pageId
      ),
      'item-id': getOptionValue(
        getItemOptionsForPage(item?.pageId ?? pageId),
        itemId
      ),
      'app-id': getOptionValue(WYSIWYG_EDITOR_APPS, appId),
      'anchor-name': anchorName,
      'link-text': '',
    };

    nextLinkFieldValues['link-text'] =
      selectedText ||
      getLinkTargetText(activeTargetKind, nextLinkFieldValues) ||
      (activeTargetKind === 'website-link' ? attrs.href : '');

    this.linkFieldValues = nextLinkFieldValues;
    this.initialLinkFieldValues = { ...this.linkFieldValues };
  }

  syncActiveLinkContext() {
    const editor = this.options.getEditor();

    if (
      !editor ||
      this.options.getIsHtmlMode() ||
      this.options.getActiveMenu()
    ) {
      this.activeLinkContext = null;

      return;
    }

    const activeButton = this.getActiveButton();
    const activeLink = activeButton ? null : this.getActiveLink();
    const activeContext = activeButton ?? activeLink;

    if (!activeContext) {
      this.activeLinkContext = null;

      return;
    }

    const shellElement = editor.view.dom.closest('.wysiwyg-editor-shell');

    /* istanbul ignore if -- The editor view is always rendered inside the shell component. */
    if (!(shellElement instanceof HTMLElement)) {
      this.activeLinkContext = null;

      return;
    }

    const contextFrom = activeContext.from;
    const contextTo = activeContext.to;
    const shellRect = shellElement.getBoundingClientRect();
    let alignment: WysiwygLinkContext['alignment'] = 'center';
    let contextX = 0;
    let contextY = 0;

    if (activeButton) {
      const buttonDom = editor.view.nodeDOM(activeButton.from);
      const buttonElement =
        buttonDom instanceof HTMLElement
          ? buttonDom.querySelector('.btn-content') ?? buttonDom
          : null;

      /* istanbul ignore if -- Active button contexts are derived from rendered button node views. */
      if (!(buttonElement instanceof HTMLElement)) {
        this.activeLinkContext = null;

        return;
      }

      const buttonRect = buttonElement.getBoundingClientRect();
      let offsetElement: HTMLElement | null = buttonElement;
      let offsetX = 0;
      let offsetY = 0;

      while (offsetElement && offsetElement !== shellElement) {
        offsetX += offsetElement.offsetLeft;
        offsetY += offsetElement.offsetTop;
        offsetElement = offsetElement.offsetParent as HTMLElement | null;
      }

      alignment = 'left';

      if (offsetElement === shellElement) {
        const scrollSurface = buttonElement.closest(
          '.wysiwyg-editor-shell__visual-surface'
        );
        const scrollTop =
          scrollSurface instanceof HTMLElement ? scrollSurface.scrollTop : 0;

        contextX = offsetX;
        contextY = offsetY + buttonElement.offsetHeight + 8 - scrollTop;
      } else {
        contextX = buttonRect.left - shellRect.left;
        contextY = buttonRect.bottom - shellRect.top + 8;
      }
    } else {
      const { selection } = editor.state;
      const cursorPosition = Math.min(
        Math.max(selection.head, contextFrom),
        contextTo
      );
      const cursorRect = editor.view.coordsAtPos(cursorPosition);

      contextX =
        cursorRect.left +
        (cursorRect.right - cursorRect.left) / 2 -
        shellRect.left;
      contextY = cursorRect.bottom - shellRect.top + 8;
    }

    const contextHalfWidth = Math.min(
      LINK_CONTEXT_POPUP_MAX_WIDTH / 2,
      Math.max(24, shellRect.width / 2 - LINK_CONTEXT_POPUP_EDGE_MARGIN)
    );
    const minContextX = contextHalfWidth + LINK_CONTEXT_POPUP_EDGE_MARGIN;
    const maxContextX =
      shellRect.width - contextHalfWidth - LINK_CONTEXT_POPUP_EDGE_MARGIN;

    this.activeLinkContext = {
      kind: activeButton
        ? activeButton.href
          ? 'button-link'
          : 'button-link-prompt'
        : activeContext.targetKind,
      targetKind: activeContext.targetKind,
      href: activeContext.href,
      label: getLinkTargetLabel(activeContext.targetKind, activeContext.attrs),
      text: activeContext.text || activeContext.href,
      from: contextFrom,
      to: contextTo,
      alignment,
      x:
        alignment === 'left'
          ? contextX
          : Math.min(Math.max(contextX, minContextX), maxContextX),
      y: contextY,
    };
  }

  getActiveButton(): WysiwygActiveButton | null {
    const editor = this.options.getEditor();

    if (!editor) return null;

    const { doc, selection } = editor.state;
    let activeButton: WysiwygActiveButton | null = null;

    doc.descendants((node, position) => {
      if (activeButton) {
        return false;
      }

      if (node.type.name !== 'wysiwygButton') {
        return true;
      }

      const buttonFrom = position;
      const buttonTo = position + node.nodeSize;
      const isCursorInsideButton =
        selection.empty &&
        selection.from > buttonFrom &&
        selection.from < buttonTo;
      const doesSelectionTouchButton =
        !selection.empty &&
        selection.from < buttonTo &&
        selection.to > buttonFrom;

      if (!isCursorInsideButton && !doesSelectionTouchButton) {
        return true;
      }

      activeButton = this.getButtonLinkFromNode(node, buttonFrom);

      return false;
    });

    return activeButton;
  }

  private getButtonAtPosition(position: number | null) {
    const editor = this.options.getEditor();

    if (!editor || position === null) return null;

    return this.getButtonLinkFromNode(
      editor.state.doc.nodeAt(position),
      position
    );
  }

  private getButtonLinkFromNode(
    node: DescendantNode | null,
    position: number
  ): WysiwygActiveButton | null {
    if (!node || node.type.name !== 'wysiwygButton') {
      return null;
    }

    const attrs = {
      href: String(node.attrs['href'] ?? ''),
      dataPage: node.attrs['dataPage'] ?? null,
      dataItem: node.attrs['dataItem'] ?? null,
      dataFolder: node.attrs['dataFolder'] ?? null,
      dataApp: node.attrs['dataApp'] ?? null,
    };

    return {
      href: attrs.href,
      attrs,
      targetKind: getLinkTargetKindFromAttrs(attrs),
      from: position,
      to: position + node.nodeSize,
      text: node.textContent.trim(),
    };
  }

  getActiveLink(): WysiwygActiveLink | null {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- This helper is only called after editor state exists. */
    if (!editor) return null;

    const { doc, schema, selection } = editor.state;
    const linkType = schema.marks['link'];

    if (!linkType) return null;

    const positions = [selection.$from.pos, selection.$to.pos];

    for (const position of positions) {
      const activeLink = this.getActiveLinkAtPosition(position);

      if (activeLink) return activeLink;
    }

    if (selection.empty) {
      return null;
    }

    let activeLink: WysiwygActiveLink | null = null;

    doc.nodesBetween(selection.from, selection.to, (node, position) => {
      if (activeLink) {
        /* istanbul ignore next -- The first active link in the selection is enough for the context popup. */
        return false;
      }

      /* istanbul ignore if -- Link scans can visit wrapper nodes before text nodes; this is a ProseMirror traversal guard. */
      if (!node.isText) {
        return;
      }

      const linkMark = node.marks.find((mark) => mark.type === linkType);

      if (!linkMark) {
        return;
      }

      const range = getMarkRange(doc.resolve(position + 1), linkType);

      /* istanbul ignore if -- A link mark found by ProseMirror should resolve to a range; this is a safety fallback. */
      if (!range) {
        return;
      }

      const attrs = {
        href: String(linkMark.attrs['href'] ?? ''),
        dataPage: linkMark.attrs['dataPage'] ?? null,
        dataItem: linkMark.attrs['dataItem'] ?? null,
        dataFolder: linkMark.attrs['dataFolder'] ?? null,
        dataApp: linkMark.attrs['dataApp'] ?? null,
      };

      activeLink = {
        href: attrs.href,
        attrs,
        targetKind: getLinkTargetKindFromAttrs(attrs),
        ...range,
        text: doc.textBetween(range.from, range.to, ' ').trim(),
      };

      return false;
    });

    return activeLink;
  }

  @action
  updateLinkField(field: string, event: Event) {
    const target = event.currentTarget;

    /* istanbul ignore if -- Link fields are wired to input/select controls in the template. */
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }

    /* istanbul ignore if -- Template callers pass only declared link field keys. */
    if (!(field in this.linkFieldValues)) {
      return;
    }

    const activeTargetKind = this.activeLinkTargetKind;
    const previousAutoLinkText = activeTargetKind
      ? getLinkTargetText(activeTargetKind, this.linkFieldValues)
      : '';
    const nextValues = {
      ...this.linkFieldValues,
      [field]: target.value,
    } as WysiwygLinkFieldValues;

    if (field === 'folder-id') {
      nextValues['page-id'] = EMPTY_LINK_SELECT_VALUE;
      nextValues['item-id'] = EMPTY_LINK_SELECT_VALUE;
    }

    if (field === 'page-id') {
      nextValues['item-id'] = EMPTY_LINK_SELECT_VALUE;
    }

    if (field !== 'link-text' && activeTargetKind) {
      const nextAutoLinkText = getLinkTargetText(activeTargetKind, nextValues);
      const currentLinkText = this.linkFieldValues['link-text'];

      if (!currentLinkText || currentLinkText === previousAutoLinkText) {
        nextValues['link-text'] = nextAutoLinkText;
      }
    }

    this.linkFieldValues = nextValues;
  }

  @action
  updateAnchorName(event: Event) {
    const target = event.currentTarget;

    /* istanbul ignore if -- Anchor-name changes come from the popup input. */
    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    this.anchorName = target.value;
  }

  @action
  openAnchorEditPopup(event: Event) {
    const detail = (event as CustomEvent).detail as
      | { anchorName?: unknown; position?: unknown }
      | undefined;

    /* istanbul ignore if -- Anchor token node views dispatch a string anchor name. */
    if (typeof detail?.anchorName !== 'string') {
      return;
    }

    /* istanbul ignore if -- Anchor token node views dispatch their numeric document position. */
    if (typeof detail.position !== 'number') {
      return;
    }

    this.anchorName = detail.anchorName;
    this.initialAnchorName = detail.anchorName;
    this.anchorEditPosition = detail.position;
    this.options.setActiveMenu('anchor-insert');
    this.activeLinkContext = null;
  }

  @action
  registerLinkPopupInput(field: string, element: Element) {
    if (field !== 'website-url' || !(element instanceof HTMLInputElement)) {
      return;
    }

    next(() => {
      element.focus();
      element.select();
    });
  }

  @action
  registerAnchorPopupInput(element: Element) {
    /* istanbul ignore if -- The template registers the anchor popup input element. */
    if (!(element instanceof HTMLInputElement)) {
      return;
    }

    next(() => {
      element.focus();
      element.select();
    });
  }

  @action
  openLinkPopup(kind: WysiwygEditorLinkPopupKind) {
    const activeButton = this.getActiveButton();
    const activeLink = this.getActiveLink();
    const existingLinkKind = activeButton?.targetKind ?? activeLink?.targetKind;
    const hasExistingLink = Boolean(activeButton?.href || activeLink);
    const popupKind =
      hasExistingLink && existingLinkKind === kind
        ? (`${kind}-edit` as WysiwygEditorEditLinkPopupKind)
        : kind;

    this.initializeLinkFields(popupKind);
    this.options.setActiveMenu(popupKind);
  }

  @action
  reopenLinkMenu() {
    this.options.setActiveMenu('link');
  }

  @action
  editActiveLinkContext(event?: Event) {
    const editor = this.options.getEditor();

    if (!editor || !this.activeLinkContext) return;

    event?.preventDefault();
    event?.stopPropagation();

    const linkContext = this.activeLinkContext;

    this.activeLinkContext = null;
    const isButtonContext = linkContext.kind.startsWith('button-link');

    editor.commands.setTextSelection({
      from: isButtonContext ? linkContext.from + 1 : linkContext.from,
      to: isButtonContext ? linkContext.to - 1 : linkContext.to,
    });

    if (linkContext.kind === 'button-link-prompt') {
      this.options.setActiveMenu('website-link');
      this.initializeLinkFields('website-link');
    } else {
      const popupKind =
        `${linkContext.targetKind}-edit` as WysiwygEditorEditLinkPopupKind;

      this.options.setActiveMenu(popupKind);
      this.initializeLinkFields(popupKind);
    }
  }

  @action
  applyLinkPopup() {
    const editor = this.options.getEditor();
    const targetKind = this.activeLinkTargetKind;

    /* istanbul ignore if -- Submit controls only render for an active link popup with a mounted editor. */
    if (!editor || !targetKind) {
      this.options.closeMenu();

      return;
    }

    if (this.isLinkPopupSubmitDisabled) {
      return;
    }

    const linkAttrs = {
      ...getEmptyLinkTargetAttrs(),
      ...getLinkTargetAttrs(targetKind, this.linkFieldValues),
    };
    const selectedText = this.getSelectedText();
    const linkText =
      this.linkFieldValues['link-text'].trim() ||
      selectedText ||
      getLinkTargetLabel(targetKind, this.linkFieldValues) ||
      linkAttrs.href;

    if (this.buttonLinkEditPosition !== null) {
      this.updateButtonLinkAtPosition(
        this.buttonLinkEditPosition,
        linkAttrs,
        linkText
      );
      this.options.closeMenu();
      this.activeLinkContext = null;

      return;
    }

    const linkedContent = `<a ${serializeLinkAttributes(
      linkAttrs
    )}>${escapeHtml(linkText)}</a>`;

    if (editor.state.selection.empty) {
      editor.chain().focus().insertContent(linkedContent).run();
    } else if (linkText !== selectedText) {
      editor.chain().focus().insertContent(linkedContent).run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink(linkAttrs).run();
    }

    this.options.closeMenu();
    this.activeLinkContext = null;
  }

  @action
  removeWebsiteLink() {
    if (this.buttonLinkEditPosition !== null) {
      this.updateButtonLinkAtPosition(
        this.buttonLinkEditPosition,
        getEmptyLinkTargetAttrs()
      );
      this.options.closeMenu();

      return;
    }

    this.options
      .getEditor()
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .run();
    this.options.closeMenu();
  }

  @action
  applyAnchorInsertPopup() {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- Submit controls are disabled until an anchor popup has valid input and a mounted editor. */
    if (!editor || this.isAnchorInsertSubmitDisabled) {
      return;
    }

    const anchorName = this.anchorName.trim();

    if (this.anchorEditPosition === null) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'wysiwygAnchor',
          attrs: {
            id: anchorName,
            name: anchorName,
          },
        })
        .run();
    } else {
      const anchorEditPosition = this.anchorEditPosition;
      let didUpdateAnchor = false;

      editor
        .chain()
        .focus()
        .command(({ state, tr }) => {
          const node = state.doc.nodeAt(anchorEditPosition);

          /* istanbul ignore if -- Anchor edit positions come from rendered anchor token node views. */
          if (node?.type.name !== 'wysiwygAnchor') {
            return false;
          }

          tr.setNodeMarkup(anchorEditPosition, undefined, {
            ...node.attrs,
            id: anchorName,
            name: anchorName,
          });
          didUpdateAnchor = true;

          return true;
        })
        .run();

      if (didUpdateAnchor) {
        editor.commands.setTextSelection(anchorEditPosition + 1);
      }
    }

    this.resetAnchorState();
    this.options.closeMenu();
    this.activeLinkContext = null;
  }

  private getSelectedText() {
    const editor = this.options.getEditor();

    if (!editor) return '';

    const { from, to } = editor.state.selection;

    return editor.state.doc.textBetween(from, to, ' ').trim();
  }

  private updateButtonLinkAtPosition(
    position: number,
    targetAttrs: WysiwygLinkTargetAttrs,
    text?: string
  ) {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- This helper is only called from editor-bound actions after the editor has mounted. */
    if (!editor) return false;

    let buttonTextEndPosition: number | null = null;
    const didUpdate = editor
      .chain()
      .focus()
      .command(({ state, tr }) => {
        const node = state.doc.nodeAt(position);

        /* istanbul ignore if -- Button edit positions come from the active button node selected by the editor. */
        if (!node || node.type.name !== 'wysiwygButton') {
          return false;
        }

        const normalizedTargetAttrs = {
          ...getEmptyLinkTargetAttrs(),
          ...targetAttrs,
        };
        const buttonAttrs = { ...node.attrs };

        delete buttonAttrs['href'];
        delete buttonAttrs['target'];
        delete buttonAttrs['dataPage'];
        delete buttonAttrs['dataItem'];
        delete buttonAttrs['dataFolder'];
        delete buttonAttrs['dataApp'];

        const linkText =
          text?.trim() || node.textContent.trim() || normalizedTargetAttrs.href;
        const attrs = {
          ...buttonAttrs,
          ...normalizedTargetAttrs,
          class: node.attrs['class'] ?? 'btn btn-content',
          target: null,
        };

        tr.replaceWith(
          position,
          position + node.nodeSize,
          node.type.create(attrs, state.schema.text(linkText))
        );
        buttonTextEndPosition = position + linkText.length + 1;

        return true;
      })
      .run();

    if (didUpdate && buttonTextEndPosition !== null) {
      editor.commands.setTextSelection({
        from: buttonTextEndPosition,
        to: buttonTextEndPosition,
      });
    }

    return didUpdate;
  }

  private getLinkAttrsInRange(from: number, to: number) {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- This helper is only called after editor state exists. */
    if (!editor) return null;

    const linkType = editor.state.schema.marks['link'];
    let attrs: WysiwygLinkTargetAttrs | null = null;

    editor.state.doc.nodesBetween(from, to, (node) => {
      if (attrs) {
        /* istanbul ignore next -- The first link mark is enough; later linked text in the range is intentionally skipped. */
        return false;
      }

      /* istanbul ignore if -- Link scans can visit wrapper nodes before text nodes; this is a ProseMirror traversal guard. */
      if (!node.isText) {
        return;
      }

      const linkMark = node.marks.find((mark) => mark.type === linkType);

      if (!linkMark) {
        return;
      }

      attrs = {
        href: String(linkMark.attrs['href'] ?? ''),
        dataPage: linkMark.attrs['dataPage'] ?? null,
        dataItem: linkMark.attrs['dataItem'] ?? null,
        dataFolder: linkMark.attrs['dataFolder'] ?? null,
        dataApp: linkMark.attrs['dataApp'] ?? null,
      };

      return false;
    });

    return attrs;
  }

  private getActiveLinkAtPosition(position: number): WysiwygActiveLink | null {
    const editor = this.options.getEditor();

    /* istanbul ignore if -- This helper is only called after editor state exists. */
    if (!editor) return null;

    const { doc, schema } = editor.state;
    const linkType = schema.marks['link'];

    if (!linkType) return null;

    const range = getMarkRange(doc.resolve(position), linkType);

    if (!range) return null;

    const attrs = this.getLinkAttrsInRange(
      range.from,
      range.to
    ) as WysiwygLinkTargetAttrs | null;

    if (!attrs || !attrs.href) return null;

    return {
      href: attrs.href,
      attrs,
      targetKind: getLinkTargetKindFromAttrs(attrs),
      ...range,
      text: doc.textBetween(range.from, range.to, ' ').trim(),
    };
  }
}
